<template>
  <div class="container-fluid py-1">
    <div class="row">
      <div class="col-12">
        <div class="card my-4 processed-files-card">
          <div class="card-header p-0 position-relative mt-2 mx-3 z-index-2">
            <div class="bg-gradient-success shadow-success border-radius-lg pt-2 pb-3">
              <h6 class="text-white text-capitalize ps-3">Processed Report Files</h6>
              <p class="text-white text-sm ps-3 mb-0">
                View zipped and unzipped processed report files grouped by license and reporting date.
              </p>
            </div>
          </div>

          <div class="card-body px-0 pb-2 processed-files-body">
            <div v-if="isLoading" class="text-center py-5 state-panel">
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted mb-0">Loading processed files...</p>
            </div>

            <div v-else-if="hasError" class="text-center py-5 state-panel">
              <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <h5 class="text-warning">Failed to load processed files</h5>
              <p class="text-muted mb-4">Please try again later.</p>
              <material-button color="success" size="sm" @click="loadProcessedFiles">
                <i class="fas fa-redo me-2"></i>
                Retry
              </material-button>
            </div>

            <div v-else-if="!processedFiles || processedFiles.length === 0" class="text-center py-5 state-panel">
              <i class="fas fa-folder-open fa-3x text-muted mb-3"></i>
              <h5 class="text-muted">No processed files found</h5>
              <p class="text-muted mb-0">Processed report files will appear here once the extraction pipeline completes.</p>
            </div>

            <div v-else class="license-list px-3 pb-3">
              <div v-for="licenseGroup in groupedByLicense" :key="licenseGroup.licenseKey" class="license-card">
                <button
                  type="button"
                  class="license-header"
                  @click="toggleLicenseGroup(licenseGroup.licenseKey)"
                >
                  <div class="license-header-main">
                    <div class="license-title-row">
                      <span class="license-title">{{ licenseGroup.licenseId || "Unassigned License" }}</span>
                      <span class="license-subtitle">{{ licenseGroup.licenseCode || "No license code" }}</span>
                    </div>
                    <div class="license-meta-row">
                      <span class="meta-pill meta-pill-success">{{ licenseGroup.totalFiles }} file(s)</span>
                      <span class="meta-pill meta-pill-muted">{{ licenseGroup.packagedCount }} packaged</span>
                      <span class="meta-pill meta-pill-warning">{{ licenseGroup.pendingCount }} pending</span>
                    </div>
                  </div>
                  <div class="license-toggle">
                    <i
                      class="fas"
                      :class="isLicenseExpanded(licenseGroup.licenseKey) ? 'fa-chevron-up' : 'fa-chevron-down'"
                    ></i>
                  </div>
                </button>

                <transition name="collapse">
                  <div v-if="isLicenseExpanded(licenseGroup.licenseKey)" class="license-body">
                    <div v-for="dateGroup in licenseGroup.dateGroups" :key="dateGroup.reportDate" class="date-group">
                      <div class="date-group-header">
                        <div>
                          <p class="date-label mb-1">Reporting Date</p>
                          <h6 class="mb-0">{{ formatDisplayDate(dateGroup.reportDate) }}</h6>
                        </div>
                        <div class="date-group-actions">
                          <span class="status-chip status-chip-packaged">
                            {{ dateGroup.packagedCount }} packaged
                          </span>
                          <span class="status-chip status-chip-pending">
                            {{ dateGroup.pendingCount }} pending
                          </span>
                          <span class="status-chip" :class="dateGroup.readyCount > 0 ? 'status-chip-ready' : 'status-chip-muted'">
                            {{ dateGroup.readyCount }} ready
                          </span>
                          <material-button
                            v-if="dateGroup.canQueue"
                            color="success"
                            size="sm"
                            class="package-button"
                            @click.stop="openPackagingConfirm(licenseGroup, dateGroup)"
                          >
                            Mark for packaging
                          </material-button>
                          <span v-else-if="dateGroup.allReady" class="status-chip status-chip-ready-all">
                            <i class="fas fa-check me-1"></i>All marked for packaging
                          </span>
                          <span
                            v-else-if="!dateGroup.requiredReportsComplete"
                            class="status-chip status-chip-missing"
                            :title="getMissingReportsTitle(dateGroup.missingRequiredReports)"
                          >
                            <i class="fas fa-exclamation-circle me-1"></i>Missing required reports
                          </span>
                        </div>
                      </div>

                      <div class="files-stack">
                        <div v-for="file in dateGroup.files" :key="file.id" class="processed-file-row">
                          <div class="processed-file-icon">
                            <i class="fas fa-file-archive"></i>
                          </div>
                          <div class="processed-file-content">
                            <div class="processed-file-topline">
                              <div>
                                <p class="processed-file-title mb-1">{{ getDisplayFileName(file.file_path) }}</p>
                                <p class="processed-file-path mb-0">{{ getDisplayFilePath(file.file_path) }}</p>
                              </div>
                              <div class="processed-file-status">
                                <span
                                  class="status-chip"
                                  :class="file.is_packaged ? 'status-chip-packaged' : 'status-chip-pending'"
                                >
                                  {{ file.status_label }}
                                </span>
                                <span
                                  v-if="file.ready_for_packaging"
                                  class="status-chip status-chip-ready"
                                >
                                  <i class="fas fa-check me-1"></i>Ready
                                </span>
                              </div>
                            </div>

                            <div class="processed-file-details">
                              <span><strong>Report:</strong> {{ file.report_id || "N/A" }}</span>
                              <span><strong>Generated:</strong> {{ formatDateTime(file.generated_at) }}</span>
                              <span><strong>License ID:</strong> {{ file.license_id || "N/A" }}</span>
                            </div>
                            <div
                              v-if="!dateGroup.requiredReportsComplete"
                              class="processed-file-details processed-file-warning"
                            >
                              <span>
                                <strong>Missing required:</strong>
                                {{ formatReportList(dateGroup.missingRequiredReports) }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div class="card-footer px-3 processed-files-footer">
            <div v-if="processedFiles && processedFiles.length > 0" class="footer-summary">
              <p class="mb-0 text-sm text-secondary">
                Showing {{ processedFiles.length }} processed report file{{ processedFiles.length === 1 ? "" : "s" }} across {{ groupedByLicense.length }} license group{{ groupedByLicense.length === 1 ? "" : "s" }}.
              </p>
            </div>
            <div v-else class="text-center text-muted">
              <small>No files to display</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showPackagingModal" class="modal-backdrop-custom" @click.self="closePackagingConfirm">
      <div class="modal-card-custom" role="dialog" aria-modal="true" aria-labelledby="package-modal-title">
        <div class="modal-header-custom">
          <h5 id="package-modal-title" class="mb-0">Confirm packaging fallback</h5>
          <button type="button" class="modal-close-button" @click="closePackagingConfirm">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body-custom">
          <p class="mb-3">
            This action marks your reporting data as ready for packaging. Background schedulers and workers will automatically detect these marked files and process them for packaging. Please ensure that all data for the reporting period has been sent in before confirming.
          </p>

          <div class="confirm-summary">
            <div>
              <strong>License ID:</strong>
              {{ packagingTarget && packagingTarget.licenseId ? packagingTarget.licenseId : "N/A" }}
            </div>
            <div>
              <strong>Reporting date:</strong>
              {{ formatDisplayDate(packagingTarget && packagingTarget.reportDate ? packagingTarget.reportDate : null) }}
            </div>
            <div>
              <strong>Pending files:</strong>
              {{ packagingTarget && packagingTarget.pendingCount ? packagingTarget.pendingCount : 0 }}
            </div>
          </div>
        </div>

        <div class="modal-footer-custom">
          <material-button color="light" size="sm" @click="closePackagingConfirm">Cancel</material-button>
          <material-button
            color="success"
            size="sm"
            :disabled="isPackaging"
            @click="confirmPackaging"
          >
            <span v-if="isPackaging">
              <i class="fas fa-spinner fa-spin me-2"></i>
              Marking...
            </span>
            <span v-else>Confirm and mark</span>
          </material-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import axios from "axios";

export default {
  name: "urp-files",
  components: {
    MaterialButton,
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      isPackaging: false,
      processedFiles: [],
      expandedLicenses: {},
      showPackagingModal: false,
      packagingTarget: null,
    };
  },
  computed: {
    groupedByLicense() {
      const groups = new Map();

      this.processedFiles.forEach((file) => {
        const licenseId = file.license_id || "Unassigned";
        const licenseCode = file.license_code || "Unknown";
        const licenseKey = `${licenseId}::${licenseCode}`;

        if (!groups.has(licenseKey)) {
          groups.set(licenseKey, {
            licenseKey,
            licenseId,
            licenseCode,
            files: [],
            dateGroupsMap: new Map(),
          });
        }

        const licenseGroup = groups.get(licenseKey);
        licenseGroup.files.push(file);

        const reportDate = file.report_date || "Unknown";
        if (!licenseGroup.dateGroupsMap.has(reportDate)) {
          licenseGroup.dateGroupsMap.set(reportDate, {
            reportDate,
            files: [],
          });
        }

        licenseGroup.dateGroupsMap.get(reportDate).files.push(file);
      });

      return Array.from(groups.values())
        .map((group) => {
          group.files.sort((a, b) => this.compareDateTime(b.generated_at, a.generated_at));

          group.dateGroups = Array.from(group.dateGroupsMap.values())
            .map((dateGroup) => {
              dateGroup.files.sort((a, b) => this.compareDateTime(b.generated_at, a.generated_at));
              dateGroup.packagedCount = dateGroup.files.filter((file) => file.is_packaged).length;
              dateGroup.readyCount = dateGroup.files.filter((file) => file.ready_for_packaging && !file.is_packaged).length;
              dateGroup.pendingCount = dateGroup.files.length - dateGroup.packagedCount;
              dateGroup.allReady = dateGroup.files.every((file) => file.ready_for_packaging || file.is_packaged);
              dateGroup.unreadyCount = dateGroup.files.filter((file) => !file.ready_for_packaging && !file.is_packaged).length;
              dateGroup.requiredReportsComplete = dateGroup.files.every(
                (file) => file.required_reports_complete !== false,
              );
              dateGroup.missingRequiredReports = dateGroup.requiredReportsComplete
                ? []
                : Array.from(
                    new Set(
                      dateGroup.files.flatMap((file) =>
                        Array.isArray(file.missing_required_reports) ? file.missing_required_reports : [],
                      ),
                    ),
                  );
              dateGroup.canQueue = dateGroup.requiredReportsComplete && dateGroup.unreadyCount > 0;
              dateGroup.latestGeneratedAt = dateGroup.files.length > 0 ? dateGroup.files[0].generated_at : null;
              return dateGroup;
            })
            .sort((a, b) => this.compareDateText(b.reportDate, a.reportDate));

          group.totalFiles = group.files.length;
          group.packagedCount = group.files.filter((file) => file.is_packaged).length;
          group.pendingCount = group.totalFiles - group.packagedCount;
          group.latestGeneratedAt = group.files.length > 0 ? group.files[0].generated_at : null;
          group.latestReportDate = group.dateGroups.length > 0 ? group.dateGroups[0].reportDate : null;

          return group;
        })
        .sort((a, b) =>
          this.compareDateText(b.latestReportDate, a.latestReportDate) ||
          this.compareDateTime(b.latestGeneratedAt, a.latestGeneratedAt) ||
          a.licenseId.localeCompare(b.licenseId),
        );
    },
  },
  created() {
    this.loadProcessedFiles();
  },
  methods: {
    compareDateTime(left, right) {
      const leftValue = left ? new Date(left).getTime() : 0;
      const rightValue = right ? new Date(right).getTime() : 0;
      const safeLeft = Number.isFinite(leftValue) ? leftValue : 0;
      const safeRight = Number.isFinite(rightValue) ? rightValue : 0;
      return safeLeft - safeRight;
    },

    compareDateText(left, right) {
      const leftText = left ? String(left) : "";
      const rightText = right ? String(right) : "";

      if (!leftText && !rightText) return 0;
      if (!leftText || leftText === "Unknown") return 1;
      if (!rightText || rightText === "Unknown") return -1;

      const leftValue = new Date(leftText).getTime();
      const rightValue = new Date(rightText).getTime();
      if (Number.isFinite(leftValue) && Number.isFinite(rightValue)) {
        return leftValue - rightValue;
      }

      if (leftText === rightText) return 0;
      return leftText > rightText ? -1 : 1;
    },

    async loadProcessedFiles() {
      this.isLoading = true;
      this.hasError = false;

      try {
        const response = await axios.get("/operator-processed-files/");

        if (
          response.data &&
          response.data.processed_files &&
          Array.isArray(response.data.processed_files)
        ) {
          this.processedFiles = response.data.processed_files;
        } else {
          console.error("Unexpected response format:", response.data);
          this.processedFiles = [];
        }
      } catch (error) {
        console.error("Error loading processed files:", error);
        this.hasError = true;
        this.processedFiles = [];

        this.showNotification("Failed to load processed files", "error");
      } finally {
        this.isLoading = false;
      }
    },

    toggleLicenseGroup(licenseKey) {
      this.expandedLicenses = {
        ...this.expandedLicenses,
        [licenseKey]: !this.expandedLicenses[licenseKey],
      };
    },

    isLicenseExpanded(licenseKey) {
      return Boolean(this.expandedLicenses[licenseKey]);
    },

    openPackagingConfirm(licenseGroup, dateGroup) {
      if (!dateGroup.requiredReportsComplete) {
        this.showNotification(
          `Required reports are still missing for ${licenseGroup.licenseId}.`,
          "warning",
        );
        return;
      }
      this.packagingTarget = {
        licenseKey: licenseGroup.licenseKey,
        licenseId: licenseGroup.licenseId,
        licenseCode: licenseGroup.licenseCode,
        reportDate: dateGroup.reportDate,
        pendingCount: dateGroup.pendingCount,
      };
      this.showPackagingModal = true;
    },

    closePackagingConfirm() {
      if (this.isPackaging) {
        return;
      }
      this.showPackagingModal = false;
      this.packagingTarget = null;
    },

    async confirmPackaging() {
      if (!this.packagingTarget || this.isPackaging) {
        return;
      }

      this.isPackaging = true;
      try {
        await axios.post("/operator-processed-files/package/", {
          license_id: this.packagingTarget.licenseId,
          report_date: this.packagingTarget.reportDate,
        });

        this.showNotification(
          `Successfully marked files for packaging: ${this.packagingTarget.licenseId} on ${this.formatDisplayDate(this.packagingTarget.reportDate)}. Background scheduler will process these shortly.`,
          "success",
        );
        this.showPackagingModal = false;
        this.packagingTarget = null;
        await this.loadProcessedFiles();
      } catch (error) {
        const message =
          error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : "Failed to mark files for packaging. Please try again later.";
        this.showNotification(message, "error");
      } finally {
        this.isPackaging = false;
      }
    },

    getDisplayFileName(filePath) {
      if (!filePath) return "N/A";
      const parts = filePath.split("/");
      return parts[parts.length - 1] || filePath;
    },

    getDisplayFilePath(filePath) {
      if (!filePath) return "";
      const parts = filePath.split("/");
      if (parts.length > 1) {
        parts.pop();
        return parts.join("/") + "/";
      }
      return "";
    },

    formatReportLabel(reportId) {
      if (!reportId) return "N/A";
      return String(reportId)
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },

    formatReportList(reportIds) {
      if (!Array.isArray(reportIds) || reportIds.length === 0) {
        return "None";
      }
      return reportIds.map((reportId) => this.formatReportLabel(reportId)).join(", ");
    },

    getMissingReportsTitle(reportIds) {
      if (!Array.isArray(reportIds) || reportIds.length === 0) {
        return "";
      }
      return `Missing required reports: ${this.formatReportList(reportIds)}`;
    },

    formatDisplayDate(dateValue) {
      if (!dateValue) return "N/A";
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return "Invalid Date";
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        console.warn("Error formatting date:", dateValue, error);
        return "Invalid Date";
      }
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "";
      try {
        const date = new Date(dateTime);
        if (isNaN(date.getTime())) return "";
        return `${date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })} ${date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })}`;
      } catch (error) {
        console.warn("Error formatting date-time:", dateTime, error);
        return "";
      }
    },

    downloadFile(file) {
      // TODO: Implement download logic
      console.log("Downloading file:", file);
      this.showNotification(`Download started for ${file.file_id}`, "info");
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          warning: "alert-warning",
          info: "alert-info",
        }[type] || "alert-info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText =
        "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
      notification.innerHTML = `
          ${message}
          <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 3000);
    },
  },
};
</script>

<style scoped>
.container-fluid {
  min-height: calc(100vh - 60px);
}

.processed-files-card {
  min-height: 500px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.processed-files-body {
  flex: 1;
}

.state-panel {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.license-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.license-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
}

.license-header {
  width: 100%;
  border: 0;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
}

.license-header:hover {
  background: #f8fbf9;
}

.license-header-main {
  flex: 1;
  min-width: 0;
}

.license-title-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  align-items: center;
}

.license-title {
  font-weight: 700;
  color: #1f2937;
}

.license-subtitle {
  color: #6b7280;
  font-size: 0.875rem;
}

.license-meta-row,
.date-group-actions,
.processed-file-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.license-meta-row {
  margin-top: 0.75rem;
}

.license-toggle {
  color: #198754;
  font-size: 1rem;
}

.license-body {
  border-top: 1px solid #edf2f7;
  padding: 1rem 1.25rem 1.25rem;
  background: #ffffff;
}

.date-group {
  border: 1px solid #edf2f7;
  border-radius: 0.9rem;
  padding: 1rem;
  background: #fcfdfd;
}

.date-group + .date-group {
  margin-top: 1rem;
}

.date-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.date-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
}

.files-stack {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.processed-file-row {
  display: flex;
  gap: 0.9rem;
  align-items: flex-start;
  padding: 0.9rem 1rem;
  border-radius: 0.8rem;
  background: #ffffff;
  border: 1px solid #eef2f7;
}

.processed-file-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e8f5e9;
  color: #dc3545;
  flex: 0 0 auto;
}

.processed-file-content {
  flex: 1;
  min-width: 0;
}

.processed-file-topline {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.processed-file-title {
  font-weight: 700;
  color: #1f2937;
  word-break: break-word;
}

.processed-file-path {
  font-size: 0.8rem;
  color: #6b7280;
  word-break: break-word;
}

.processed-file-details {
  margin-top: 0.7rem;
  font-size: 0.82rem;
  color: #4b5563;
}

.processed-file-warning {
  color: #9a6700;
}

.status-chip,
.meta-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-chip-packaged,
.meta-pill-success {
  background: rgba(25, 135, 84, 0.12);
  color: #157347;
}

.status-chip-pending,
.meta-pill-warning {
  background: rgba(255, 193, 7, 0.18);
  color: #8a6d00;
}

.status-chip-ready {
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
}

.status-chip-ready-all {
  background: rgba(25, 135, 84, 0.2);
  color: #157347;
}

.status-chip-missing {
  background: rgba(255, 193, 7, 0.18);
  color: #9a6700;
}

.meta-pill-muted {
  background: rgba(108, 117, 125, 0.12);
  color: #495057;
}

.processed-files-footer {
  margin-top: auto;
  background-color: transparent;
  border-top: 1px solid #e9ecef;
  padding: 1rem 0;
}

.footer-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(15, 23, 42, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-card-custom {
  width: min(640px, 100%);
  background: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
}

.modal-header-custom,
.modal-footer-custom {
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.modal-header-custom {
  border-bottom: 1px solid #edf2f7;
}

.modal-body-custom {
  padding: 1.25rem;
}

.modal-footer-custom {
  border-top: 1px solid #edf2f7;
}

.modal-close-button {
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 1rem;
}

.confirm-summary {
  display: grid;
  gap: 0.5rem;
  padding: 0.9rem 1rem;
  border-radius: 0.85rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}

.bg-gradient-success {
  background: linear-gradient(310deg, #198754 0%, #2ecc71 100%);
}

.shadow-success {
  box-shadow:
    0 4px 20px 0 rgba(25, 135, 84, 0.14),
    0 7px 10px -5px rgba(25, 135, 84, 0.4);
}

.package-button {
  white-space: nowrap;
}

@media (max-width: 768px) {
  .license-header,
  .date-group-header,
  .processed-file-topline,
  .modal-header-custom,
  .modal-footer-custom {
    flex-direction: column;
    align-items: flex-start;
  }

  .processed-file-row {
    padding: 0.85rem;
  }

  .processed-file-icon {
    width: 32px;
    height: 32px;
  }
}
</style>
